import {
  Bot,
  ArrowDownToLine,
  CalendarClock,
  Clock3,
  ChevronLeft,
  ChevronRight,
  Coins,
  FileText,
  History,
  Image as ImageIcon,
  Info,
  ListChecks,
  Megaphone,
  Mic,
  PenLine,
  Phone,
  Search,
  Sparkles,
  UserPlus,
  Video,
  Wand2,
  LayoutGrid,
  Mail,
  MessageCircle,
  Plus,
  Receipt,
  Repeat,
  ShoppingCart,
  TrendingDown,
  X,
} from "lucide-react";
import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, Button, EmptyState, PageLayout } from "../components";
import {
  SettingsCostsSkeleton,
  SettingsHistoryRowsSkeleton,
  SettingsPlansSkeleton,
  SettingsUsageSummarySkeleton,
  SettingsSkeleton,
} from "../components/skeletons";
import {
  CARD_ACTION,
  CARD_PRIMARY,
} from "../components/organization/actionStyles";
import { useActiveContext } from "../contexts/ActiveContextContext";
import { useTranslation } from "../contexts/LanguageContext";
import { cn } from "../utils/cn";

import apiService, { API_ENDPOINTS } from "../shared/apiService";
import { showToastWarning } from "../shared/alertService";
import type {
  CreditsPlansTab,
  SendProduct,
  Subscription,
} from "../components/CreditsPlansPanel";

// Só é baixado quando o usuário abre a aba de compra.
const CreditsPlansPanel = lazy(() =>
  import("../components/CreditsPlansPanel").then((m) => ({
    default: m.CreditsPlansPanel,
  })),
);

/** Aonde levar o usuário na aba de compra: cada botão abre direto o que ele quer comprar. */
interface BuyTarget {
  segment: CreditsPlansTab;
  product?: SendProduct;
}

// Aba, segmento e produto ficam na URL (?aba=planos&tipo=envios&produto=whatsapp), para que
// qualquer tela ou e-mail possa linkar direto para a compra.
const TAB_SLUGS: Record<string, TabId> = {
  historico: "history",
  custos: "costs",
  planos: "plans",
};
const SEGMENT_SLUGS: Record<string, CreditsPlansTab> = {
  assinatura: "subscriptions",
  recarga: "paygo",
  envios: "sends",
};
const slugOf = <T extends string>(map: Record<string, T>, value: T) =>
  Object.keys(map).find((key) => map[key] === value);

// --- Dashboard de Créditos (IA, imagem, post) ---
const USAGE_TYPE_LABELS: Record<string, string> = {
  image_generation: "Geração de imagem",
  enhance_image: "Melhoria de imagem",
  video_generation: "Geração de vídeo",
  post_ai: "Post com IA",
  content_ai: "Conteúdo com IA",
  content_process: "Processamento de conteúdo",
  visual_suggestions: "Sugestões visuais",
  auto_responder_ai: "Auto-resposta IA",
  ebook_ai: "Ebook com IA",
  manual_post_creation: "Criação de post",
  manual_post_scheduling: "Agendamento de post",
  deep_research: "Deep Research",
  paid_ads_creation: "Anúncio Pago (Base)",
  paid_ads_capi: "Anúncio Pago (CAPI/Pixel)",
  lead_capture: "Captura de Lead",
  whatsapp_template_send: "Envio de template (WhatsApp)",
  whatsapp_call_permission: "Pedido de permissão de ligação",
  whatsapp_transcription: "Transcrição de áudio (WhatsApp)",
  whatsapp_call: "Ligação (WhatsApp)",
  whatsapp_ai_response: "Resposta com IA (WhatsApp)",
  whatsapp_ai_assist: "Assistente de IA (WhatsApp)",
  whatsapp_task_detection: "Detecção de tarefas (WhatsApp)",
  email_campaign_send: "Envio de campanha de e-mail",
  email_ai_reply: "Resposta com IA (e-mail)",
  default: "Uso de IA",
};

/**
 * Ações cobradas do saldo de envios (pacotes/franquia), não dos créditos de IA: o backend só
 * mantém um valor em créditos como fallback de exibição, então ficam fora da tabela de custos.
 */
const SEND_BILLED_TYPES = new Set([
  "whatsapp_template_send",
  "email_campaign_send",
]);

/** Áreas da tabela de custos (ordem de exibição); tipos sem área vão para "Outros". */
const COST_GROUPS: { label: string; types: string[] }[] = [
  {
    label: "Conteúdo e posts",
    types: [
      "post_ai",
      "content_ai",
      "content_process",
      "ebook_ai",
      "deep_research",
      "manual_post_creation",
      "manual_post_scheduling",
    ],
  },
  {
    label: "Imagem e vídeo",
    types: [
      "image_generation",
      "enhance_image",
      "video_generation",
      "visual_suggestions",
    ],
  },
  {
    label: "Anúncios e leads",
    types: ["paid_ads_creation", "paid_ads_capi", "lead_capture"],
  },
  {
    label: "WhatsApp e atendimento",
    types: [
      "auto_responder_ai",
      "whatsapp_ai_response",
      "whatsapp_ai_assist",
      "whatsapp_task_detection",
      "whatsapp_transcription",
      "whatsapp_call",
      "whatsapp_call_permission",
      "email_ai_reply",
    ],
  },
];

type TabId = "overview" | "history" | "costs" | "plans";

interface CreditsDashboardProps {
  balance: number | null;
  history: any[];
  costs: Record<string, number>;
  /** A tabela de custos não carregou: sem isso a aba ficaria no skeleton para sempre. */
  costsError?: boolean;
  loading: boolean;
  total: number;
  page: number;
  totalPages: number;
  consumed: number;
  /** Consumo dos últimos 30 dias: base da projeção de duração do saldo. */
  consumedLast30d: number;
  /** As 5 atividades mais recentes (busca própria, sem os filtros do Histórico). */
  recent: any[];
  recentLoading: boolean;
  productBalances: Record<"email" | "whatsapp", ProductBalance>;
  onPageChange: (p: number) => void;
  /** Filtros do histórico (tipo de uso e período) e os tipos que existem para filtrar. */
  historyFilter: HistoryFilter;
  onHistoryFilterChange: (filter: HistoryFilter) => void;
  usageTypes: string[];
  /** Plano vigente (do /balance), repassado para a aba de compra não buscar de novo. */
  subscription: Subscription | null;
  onExportCsv: () => void;
  exporting: boolean;
  usageSummary: UsageSummary | null;
  usageSummaryLoading: boolean;
  allowanceRenewsAt: string | null;
  /** Multiplicador de custo por modelo de IA (custos de texto variam com o modelo). */
  llmMultipliers: Record<string, number>;
  /** Pagamento aberto em outra aba: a página atualiza o saldo quando o usuário voltar. */
  onCheckoutOpened: () => void;
  /** Atraso do pagamento da assinatura de créditos (carência e bloqueio da IA). */
  paymentOverdue: PaymentOverdue | null;
}

interface PaymentOverdue {
  since: string;
  blocks_at: string;
  blocked: boolean;
}

const CYCLE_LABELS: Record<string, string> = {
  MONTHLY: "Mensal",
  QUARTERLY: "Trimestral",
  SEMIANNUALLY: "Semestral",
  YEARLY: "Anual",
};

interface HistoryFilter {
  /** "usage" = consumo (saídas); "purchases" = compras e bônus (entradas). */
  kind: "usage" | "purchases";
  usageType: string;
  /** Últimos N dias (só os presets que a API aceita); null = todo o período. */
  days: 7 | 30 | 90 | null;
}

const PRODUCT_UNITS: Record<string, string> = {
  email: "e-mails",
  whatsapp: "envios de WhatsApp",
};

/** Filtros do histórico na URL (?ver=entradas&uso=post_ai&periodo=30). */
export function readHistoryFilter(params: URLSearchParams): HistoryFilter {
  const days = Number(params.get("periodo"));
  return {
    kind: params.get("ver") === "entradas" ? "purchases" : "usage",
    usageType: params.get("uso") ?? "",
    days: days === 7 || days === 30 || days === 90 ? days : null,
  };
}

const HISTORY_TIME: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
};

/** Agrupa linhas por dia ("Hoje", "Ontem", "15 de out."), mantendo a ordem da API. */
export function groupByDay(
  rows: any[],
  t: (text: string) => string,
): { key: string; label: string; rows: any[] }[] {
  const today = new Date();
  const dayKey = (d: Date) =>
    `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const groups: { key: string; label: string; rows: any[] }[] = [];
  for (const row of rows) {
    const date = row.created_at ? new Date(row.created_at) : null;
    const key = date ? dayKey(date) : "sem-data";
    let group = groups[groups.length - 1];
    if (!group || group.key !== key) {
      const label = !date
        ? "-"
        : key === dayKey(today)
          ? t("Hoje")
          : key === dayKey(yesterday)
            ? t("Ontem")
            : date.toLocaleDateString("pt-BR", {
                day: "numeric",
                month: "short",
                ...(date.getFullYear() !== today.getFullYear()
                  ? { year: "numeric" }
                  : {}),
              });
      group = { key, label, rows: [] };
      groups.push(group);
    }
    group.rows.push(row);
  }
  return groups;
}

/** Lista do histórico com separador por dia (o horário fica na linha). */
const DayGroupedList: React.FC<{
  rows: any[];
  renderRow: (row: any, index: number) => React.ReactNode;
}> = ({ rows, renderRow }) => {
  const { t } = useTranslation();
  return (
    <div>
      {groupByDay(rows, t).map((group) => (
        <section key={group.key} aria-label={group.label}>
          <h4 className="sticky top-0 bg-gray-50/90 px-4 py-1.5 text-[11px] font-bold uppercase tracking-wide text-gray-500 border-y border-gray-100 first:border-t-0">
            {group.label}
          </h4>
          <ul className="divide-y divide-gray-50">
            {group.rows.map((row, i) => renderRow(row, i))}
          </ul>
        </section>
      ))}
    </div>
  );
};

const HISTORY_PERIODS: { days: HistoryFilter["days"]; label: string }[] = [
  { days: null, label: "Tudo" },
  { days: 7, label: "7 dias" },
  { days: 30, label: "30 dias" },
  { days: 90, label: "90 dias" },
];

/** Envios disponíveis de um produto: franquia do mês (assinatura) + pacotes comprados. */
interface ProductBalance {
  allowance: number;
  package: number;
  total: number;
}

const EMPTY_PRODUCT_BALANCE: ProductBalance = {
  allowance: 0,
  package: 0,
  total: 0,
};

const fmt = (n: number) => n.toLocaleString("pt-BR");

/** Chip de saldo no cabeçalho; vermelho quando zerado. */
const BalanceChip: React.FC<{
  icon: React.ElementType;
  value: number;
  label: string;
  tone?: "ok" | "low" | "empty";
}> = ({ icon: Icon, value, label, tone = "ok" }) => (
  <span
    className={cn(
      "inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 text-xs",
      tone === "empty"
        ? "border-rose-200 bg-rose-50 text-rose-700"
        : tone === "low"
          ? "border-amber-200 bg-amber-50 text-amber-700"
          : "border-gray-100 bg-gray-50/80 text-gray-600",
    )}
  >
    <Icon className="w-3.5 h-3.5 shrink-0 opacity-70" />
    <span className="font-bold tabular-nums">{fmt(value)}</span>
    {label}
  </span>
);

/** KPI compacto (§6 do design system). */
const Kpi: React.FC<{
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  hint?: string;
  valueClass?: string;
}> = ({ icon: Icon, label, value, hint, valueClass }) => (
  <div className="rounded-2xl border border-purple-200 bg-white px-4 py-3">
    <div className="flex items-center gap-2 text-xs font-semibold text-gray-500">
      <Icon className="w-4 h-4 text-gray-400" />
      {label}
    </div>
    <p
      className={cn(
        "mt-1 text-2xl font-black tracking-tight tabular-nums",
        valueClass ?? "text-gray-900",
      )}
    >
      {value}
    </p>
    {hint && <p className="text-xs text-gray-400">{hint}</p>}
  </div>
);

/** Saldo de envios: sólido com saldo, tracejado com CTA quando zerado. */
const ProductCard: React.FC<{
  icon: React.ElementType;
  label: string;
  value: ProductBalance;
  onBuy?: () => void | Promise<void>;
  /** Próxima renovação da franquia (ISO), se houver assinatura do Virtus Chat. */
  renewsAt?: string | null;
}> = ({ icon: Icon, label, value, onBuy, renewsAt }) => {
  const { t } = useTranslation();
  const empty = value.total <= 0;
  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-2xl px-4 py-3",
        empty
          ? "border border-dashed border-purple-200 bg-white"
          : "border border-purple-200 bg-white",
      )}
    >
      <div
        className={cn(
          "w-9 h-9 shrink-0 rounded-xl flex items-center justify-center",
          empty ? "bg-gray-100 text-gray-400" : "bg-purple-50 text-purple-600",
        )}
      >
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900">{label}</p>
        <p className="text-xs text-gray-500">
          {empty
            ? t("Sem envios disponíveis")
            : `${t("Franquia do mês")}: ${fmt(value.allowance)} · ${t("Pacotes")}: ${fmt(value.package)}`}
          {renewsAt &&
            ` · ${t("Renova em {date}", {
              date: new Date(renewsAt).toLocaleDateString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
              }),
            })}`}
        </p>
      </div>
      {empty ? (
        onBuy && (
          <button type="button" onClick={onBuy} className={CARD_ACTION}>
            <Plus className="w-3.5 h-3.5" />
            {t("Adicionar")}
          </button>
        )
      ) : (
        <p className="text-lg font-black text-purple-700 tabular-nums">
          {fmt(value.total)}{" "}
          <span className="text-xs font-semibold text-gray-400">
            {t("envios")}
          </span>
        </p>
      )}
    </div>
  );
};

/** Linha do histórico de consumo: tipo de uso, data/descrição e débito. */
/** Ícone de cada tipo de uso no histórico (moeda para tipos sem ícone próprio). */
const USAGE_TYPE_ICONS: Record<string, React.ElementType> = {
  image_generation: ImageIcon,
  enhance_image: Wand2,
  video_generation: Video,
  post_ai: Sparkles,
  content_ai: FileText,
  content_process: FileText,
  visual_suggestions: ImageIcon,
  auto_responder_ai: Bot,
  ebook_ai: FileText,
  manual_post_creation: PenLine,
  manual_post_scheduling: CalendarClock,
  deep_research: Search,
  paid_ads_creation: Megaphone,
  paid_ads_capi: Megaphone,
  lead_capture: UserPlus,
  whatsapp_template_send: MessageCircle,
  whatsapp_call_permission: Phone,
  whatsapp_transcription: Mic,
  whatsapp_call: Phone,
  whatsapp_ai_response: Bot,
  whatsapp_ai_assist: Bot,
  whatsapp_task_detection: ListChecks,
  email_campaign_send: Mail,
  email_ai_reply: Mail,
};

/**
 * Descrição legível para o usuário: tira marcadores e detalhes internos que o backend grava
 * junto ("[reserva]", "(job <uuid>)", "(modelo: ..., N créditos)"). O registro no banco não muda.
 */
export function cleanUsageDescription(description?: string | null): string {
  if (!description) return "";
  return description
    .replace(/\[[^\]]*\]\s*/g, "")
    .replace(/\s*\((?:job|modelo|model)\b[^)]*\)/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

const HistoryRow: React.FC<{ row: any }> = ({ row }) => {
  const { t } = useTranslation();
  const Icon = USAGE_TYPE_ICONS[row.usage_type] ?? Coins;
  const description = cleanUsageDescription(row.description);
  const refunded = Boolean(row.refunded_at);
  return (
    <li className="flex items-center gap-3 px-4 py-3 hover:bg-purple-50/30 transition-colors">
      <div className="w-9 h-9 shrink-0 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">
          {row.usage_type
            ? t(USAGE_TYPE_LABELS[row.usage_type] || row.usage_type)
            : "-"}
        </p>
        <p className="text-xs text-gray-500 truncate">
          {row.created_at
            ? new Date(row.created_at).toLocaleTimeString("pt-BR", HISTORY_TIME)
            : "-"}
          {description && ` · ${description}`}
        </p>
      </div>
      {refunded ? (
        // Estorno (ex.: reserva devolvida porque a geração falhou): o saldo já voltou.
        <span
          className="shrink-0 flex items-center gap-2"
          title={row.refund_reason || undefined}
        >
          <span className="rounded-md bg-emerald-50 px-1.5 py-0.5 text-[11px] font-bold text-emerald-700">
            {t("Estornado")}
          </span>
          <span className="font-bold text-gray-400 line-through tabular-nums">
            -{row.amount}
          </span>
        </span>
      ) : (
        <span className="shrink-0 font-bold text-rose-600 tabular-nums">
          -{row.amount}{" "}
          <span className="text-xs font-semibold text-gray-500">
            {t(row.amount === 1 ? "crédito" : "créditos")}
          </span>
        </span>
      )}
    </li>
  );
};

/** Destaca o termo buscado no nome (sem diferenciar maiúsculas). */
const HighlightMatch: React.FC<{ text: string; query: string }> = ({
  text,
  query,
}) => {
  const q = query.trim();
  const index = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (index < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, index)}
      <mark className="rounded-sm bg-purple-100 px-0.5 text-purple-900">
        {text.slice(index, index + q.length)}
      </mark>
      {text.slice(index + q.length)}
    </>
  );
};

/** Ações mostradas para quem ainda não usou créditos (as mais comuns). */
const STARTER_ACTIONS = ["post_ai", "image_generation", "video_generation"];

interface UsageSummary {
  days: number;
  by_type: { usage_type: string; amount: number }[];
  daily: { date: string; amount: number }[];
}

/**
 * Consumo dos últimos 30 dias: barras diárias (uma série, uma cor) e "para onde vão os
 * créditos" (participação por tipo). Valores e rótulos em tinta de texto; a cor fica na marca.
 */
const UsageSummaryCard: React.FC<{ summary: UsageSummary }> = ({ summary }) => {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState<number | null>(null);
  const total = summary.by_type.reduce((sum, item) => sum + item.amount, 0);
  const max = Math.max(1, ...summary.daily.map((d) => d.amount));
  const average =
    summary.daily.reduce((sum, d) => sum + d.amount, 0) /
    Math.max(1, summary.daily.length);
  const top = summary.by_type.slice(0, 5);
  const rest = total - top.reduce((sum, item) => sum + item.amount, 0);
  const shares =
    rest > 0 ? [...top, { usage_type: "__other", amount: rest }] : top;
  const day = (iso: string) =>
    new Date(`${iso}T12:00:00`).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
    });
  const active = hovered !== null ? summary.daily[hovered] : null;

  return (
    <div className="rounded-2xl border border-purple-200 bg-white p-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="flex flex-col">
          <div className="flex items-baseline justify-between gap-3">
            <h3 className="text-sm font-bold text-gray-900">
              {t("Consumo diário")}
            </h3>
            <p
              className="text-xs text-gray-500 tabular-nums"
              aria-live="polite"
            >
              {active
                ? t("{date}: {amount} créditos", {
                    date: day(active.date),
                    amount: fmt(active.amount),
                  })
                : t("últimos {days} dias", { days: summary.days })}
            </p>
          </div>
          {/* Eixo com o máximo e o zero + linha da média: dá escala mesmo quando um dia
              muito acima dos outros achata as barras. */}
          <div className="mt-3 flex flex-1 min-h-32 gap-2">
            <div className="flex flex-col justify-between pb-px text-[11px] text-gray-400 tabular-nums text-right">
              <span>{fmt(max)}</span>
              <span>0</span>
            </div>
            <div className="relative flex-1">
              <div className="absolute inset-x-0 top-0 border-t border-dashed border-gray-100" />
              {average > 0 && (
                <div
                  className="pointer-events-none absolute inset-x-0 border-t border-dashed border-gray-400"
                  style={{ bottom: `${(average / max) * 100}%` }}
                >
                  <span className="absolute right-0 -top-4 rounded bg-white/90 px-1 text-[11px] text-gray-500 tabular-nums">
                    {t("média {value}/dia", {
                      value: fmt(Math.round(average)),
                    })}
                  </span>
                </div>
              )}
              <div
                className="flex h-full items-end gap-[2px] border-b border-gray-200"
                role="img"
                aria-label={t("Consumo diário nos últimos {days} dias", {
                  days: summary.days,
                })}
                onMouseLeave={() => setHovered(null)}
              >
                {summary.daily.map((d, i) => (
                  <div
                    key={d.date}
                    className="flex h-full flex-1 items-end"
                    onMouseEnter={() => setHovered(i)}
                    title={`${day(d.date)}: ${fmt(d.amount)}`}
                  >
                    <div
                      className={cn(
                        "w-full rounded-t-[4px] transition-colors",
                        hovered === i ? "bg-purple-700" : "bg-purple-500",
                      )}
                      style={{
                        height: d.amount > 0 ? `${(d.amount / max) * 100}%` : 0,
                        minHeight: d.amount > 0 ? 2 : 0,
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-1 flex justify-between pl-8 text-[11px] text-gray-400 tabular-nums">
            <span>{day(summary.daily[0].date)}</span>
            <span>{t("Hoje")}</span>
          </div>
          {/* Alternativa em texto do gráfico, para leitores de tela. */}
          {/* O sr-only fica num div: numa <table> o height: 1px é ignorado (a tabela
              cresce até o conteúdo) e as 30 linhas invisíveis aumentavam a rolagem da página. */}
          <div className="sr-only">
            <table>
              <caption>{t("Consumo diário")}</caption>
              <tbody>
                {summary.daily.map((d) => (
                  <tr key={d.date}>
                    <th scope="row">{day(d.date)}</th>
                    <td>{d.amount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-bold text-gray-900">
            {t("Para onde vão os créditos")}
          </h3>
          <ul className="mt-3 space-y-2.5">
            {shares.map((item) => {
              const pct =
                total > 0 ? Math.round((item.amount / total) * 100) : 0;
              const Icon =
                item.usage_type === "__other"
                  ? Coins
                  : (USAGE_TYPE_ICONS[item.usage_type] ?? Coins);
              return (
                <li key={item.usage_type}>
                  <div className="flex items-center gap-2 text-sm">
                    <Icon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <span className="flex-1 truncate text-gray-700">
                      {item.usage_type === "__other"
                        ? t("Outros")
                        : t(
                            USAGE_TYPE_LABELS[item.usage_type] ||
                              item.usage_type,
                          )}
                    </span>
                    <span className="text-xs text-gray-500 tabular-nums">
                      {fmt(item.amount)}
                    </span>
                    <span className="w-10 text-right text-sm font-bold text-gray-900 tabular-nums">
                      {pct}%
                    </span>
                  </div>
                  <div className="mt-1 h-1.5 rounded-full bg-purple-50">
                    <div
                      className="h-full rounded-full bg-purple-500"
                      style={{ width: `${Math.max(pct, 1)}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

/** Entrada de saldo: recarga, plano, pacote de envios ou bônus. */
const PurchaseRow: React.FC<{ row: any }> = ({ row }) => {
  const { t } = useTranslation();
  const description = (row.description || t("Créditos adicionados")).replace(
    /^Pagamento via Asaas:\s*/i,
    "",
  );
  const unit = row.product ? PRODUCT_UNITS[row.product] : "créditos";
  return (
    <li className="flex items-center gap-3 px-4 py-3 hover:bg-emerald-50/30 transition-colors">
      <div className="w-9 h-9 shrink-0 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
        <ShoppingCart className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">
          {description}
        </p>
        <p className="text-xs text-gray-500 truncate">
          {row.created_at
            ? new Date(row.created_at).toLocaleTimeString("pt-BR", HISTORY_TIME)
            : "-"}
        </p>
      </div>
      <span className="shrink-0 font-bold text-emerald-600 tabular-nums">
        +{fmt(row.amount)}{" "}
        <span className="text-xs font-semibold text-emerald-500">
          {t(unit ?? "créditos")}
        </span>
      </span>
    </li>
  );
};

const CreditsDashboard: React.FC<CreditsDashboardProps> = ({
  balance,
  history,
  costs,
  costsError,
  loading,
  total,
  page,
  totalPages,
  consumed,
  consumedLast30d,
  recent,
  recentLoading,
  productBalances,
  onPageChange,
  historyFilter,
  onHistoryFilterChange,
  usageTypes,
  subscription,
  onExportCsv,
  exporting,
  usageSummary,
  usageSummaryLoading,
  allowanceRenewsAt,
  llmMultipliers,
  onCheckoutOpened,
  paymentOverdue,
}) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TAB_SLUGS[searchParams.get("aba") ?? ""] ?? "overview";
  const segment =
    SEGMENT_SLUGS[searchParams.get("tipo") ?? ""] ?? "subscriptions";
  const product: SendProduct =
    searchParams.get("produto") === "email" ? "email" : "whatsapp";

  /** Troca de aba entra no histórico (o Voltar do navegador funciona). */
  const setTab = (id: TabId) =>
    setSearchParams(id === "overview" ? {} : { aba: slugOf(TAB_SLUGS, id)! });
  /** Segmento/produto dentro da aba de compra só substituem a URL atual. */
  const setPlansParams = (next: BuyTarget) =>
    setSearchParams(
      {
        aba: "planos",
        tipo: slugOf(SEGMENT_SLUGS, next.segment)!,
        ...(next.segment === "sends"
          ? { produto: next.product ?? product }
          : {}),
      },
      { replace: tab === "plans" },
    );
  const goToPlans = (target: BuyTarget = { segment: "subscriptions" }) =>
    setPlansParams(target);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const creditsBalance = balance ?? 0;
  const hasHistoryFilter =
    historyFilter.usageType !== "" || historyFilter.days !== null;
  const isPurchases = historyFilter.kind === "purchases";
  // Conta que ainda não consumiu nada: mostra os primeiros passos no lugar da atividade.
  const isNewAccount = !recentLoading && recent.length === 0 && consumed === 0;
  const [costQuery, setCostQuery] = useState("");
  // Ordenação na URL, como os filtros do histórico (sobrevive ao recarregar).
  const costSort: "price" | "usage" | "name" =
    searchParams.get("ordem") === "uso"
      ? "usage"
      : searchParams.get("ordem") === "nome"
        ? "name"
        : "price";
  const setCostSort = (next: "price" | "usage" | "name") =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === "price") params.delete("ordem");
        else params.set("ordem", next === "usage" ? "uso" : "nome");
        return params;
      },
      { replace: true },
    );
  // Notas abertas no desktop e recolhidas no celular (ocupam muito espaço em tela pequena).
  const [notesOpen, setNotesOpen] = useState(
    () =>
      typeof window === "undefined" ||
      window.matchMedia("(min-width: 640px)").matches,
  );
  const modelMultipliers = Object.entries(llmMultipliers)
    .filter(([model]) => model !== "default")
    .sort(([, a], [, b]) => b - a);
  const maxLlmMultiplier = Math.max(1, ...modelMultipliers.map(([, m]) => m));
  // Projeção pelo ritmo dos últimos 30 dias; null = sem consumo recente para estimar.
  const dailyUse = consumedLast30d / 30;
  const daysLeft = dailyUse > 0 ? Math.floor(creditsBalance / dailyUse) : null;
  // Baixo = acaba em menos de uma semana no ritmo atual (ou, sem ritmo, ≤ 20 créditos).
  const isLow =
    creditsBalance > 0 &&
    (daysLeft !== null ? daysLeft < 7 : creditsBalance <= 20);
  const isEmpty = creditsBalance <= 0;
  const balanceTone = isEmpty ? "empty" : isLow ? "low" : "ok";

  // Só ações cobradas em créditos de IA (os envios saem do saldo de envios).
  const costEntries = Object.entries(costs)
    .filter(([key]) => key !== "default" && !SEND_BILLED_TYPES.has(key))
    .sort(([, a], [, b]) => b - a);
  const costQueryNorm = costQuery.trim().toLowerCase();
  const visibleCosts = costQueryNorm
    ? costEntries.filter(([key]) =>
        t(USAGE_TYPE_LABELS[key] || key)
          .toLowerCase()
          .includes(costQueryNorm),
      )
    : costEntries;
  // Vezes que cada ação foi usada nos últimos 30 dias (aproximado: créditos ÷ custo base).
  const usedTimes = (key: string) => {
    const spent = usageSummary?.by_type.find(
      (b) => b.usage_type === key,
    )?.amount;
    return spent && costs[key]
      ? Math.max(1, Math.round(spent / costs[key]))
      : 0;
  };
  const sortCosts = (entries: [string, number][]) =>
    [...entries].sort(([ka, va], [kb, vb]) =>
      costSort === "name"
        ? t(USAGE_TYPE_LABELS[ka] || ka).localeCompare(
            t(USAGE_TYPE_LABELS[kb] || kb),
            "pt-BR",
          )
        : costSort === "usage"
          ? usedTimes(kb) * vb - usedTimes(ka) * va || vb - va
          : vb - va,
    );
  const groupedKeys = new Set(COST_GROUPS.flatMap((g) => g.types));
  const costGroups = [
    ...COST_GROUPS.map((group) => ({
      label: group.label,
      entries: sortCosts(
        visibleCosts.filter(([key]) => group.types.includes(key)),
      ),
    })),
    {
      label: "Outros",
      entries: sortCosts(visibleCosts.filter(([key]) => !groupedKeys.has(key))),
    },
  ].filter((group) => group.entries.length > 0);

  // Abas de navegação (a de compra também tem o botão-CTA no cabeçalho).
  const tabs: {
    id: TabId;
    label: string;
    icon: React.ReactNode;
    count?: string;
  }[] = [
    { id: "overview", label: t("Visão geral"), icon: <LayoutGrid /> },
    {
      id: "history",
      label: t("Histórico"),
      icon: <History />,
      count: total > 0 ? fmt(total) : undefined,
    },
    {
      id: "costs",
      label: t("Custos por ação"),
      icon: <Receipt />,
      count: costEntries.length > 0 ? String(costEntries.length) : undefined,
    },
    // Duplica o botão do cabeçalho de propósito: quem navega pelas abas também acha a compra.
    { id: "plans", label: t("Comprar créditos"), icon: <ShoppingCart /> },
  ];

  // Padrão WAI-ARIA de tablist: setas/Home/End movem o foco e ativam a aba.
  const handleTabKeyDown = (e: React.KeyboardEvent, index: number) => {
    const last = tabs.length - 1;
    const target =
      e.key === "ArrowRight"
        ? index === last
          ? 0
          : index + 1
        : e.key === "ArrowLeft"
          ? index === 0
            ? last
            : index - 1
          : e.key === "Home"
            ? 0
            : e.key === "End"
              ? last
              : null;
    if (target === null) return;
    e.preventDefault();
    tabRefs.current[target]?.focus();
    setTab(tabs[target].id);
  };

  return (
    <div className="space-y-4">
      {/* Cabeçalho compacto com saldos e abas (mesmo padrão da Sede Corporativa) */}
      <section className="bg-white border border-purple-200 rounded-3xl overflow-hidden">
        <div className="h-1.5 bg-linear-to-r from-indigo-600 via-purple-600 to-pink-600" />
        <div className="px-4 sm:px-5">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-12 h-12 shrink-0 rounded-xl bg-purple-50 border border-purple-100 flex items-center justify-center">
                <Coins className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-black tracking-tight text-gray-900">
                  {t("Seu saldo")}
                </h2>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  <BalanceChip
                    icon={Coins}
                    value={creditsBalance}
                    label={t("créditos")}
                    tone={balanceTone}
                  />
                  <BalanceChip
                    icon={MessageCircle}
                    value={productBalances.whatsapp.total}
                    label={t("WhatsApp")}
                    tone={productBalances.whatsapp.total > 0 ? "ok" : "empty"}
                  />
                  <BalanceChip
                    icon={Mail}
                    value={productBalances.email.total}
                    label={t("e-mails")}
                    tone={productBalances.email.total > 0 ? "ok" : "empty"}
                  />
                  {subscription && (
                    <span className="inline-flex items-center gap-1.5 rounded-lg border border-purple-200 bg-purple-50 px-2 py-1 text-xs font-semibold text-purple-700">
                      <Repeat className="w-3.5 h-3.5 shrink-0" />
                      {t("Plano {plan} · {cycle}", {
                        plan:
                          subscription.plan.charAt(0).toUpperCase() +
                          subscription.plan.slice(1),
                        cycle: t(CYCLE_LABELS[subscription.cycle] ?? "Mensal"),
                      })}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {/* CTA da página ao lado do saldo; no celular desce para baixo dos chips. */}
            <button
              type="button"
              onClick={() => setTab("plans")}
              aria-current={tab === "plans" ? "page" : undefined}
              className={cn(
                // Roxo sólido: a faixa do topo fica como único degradê do card.
                "relative w-full sm:w-auto flex items-center justify-center gap-2 h-10 px-5 rounded-xl text-sm font-bold whitespace-nowrap transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-purple-400",
                tab === "plans"
                  ? "bg-purple-100 text-purple-700 ring-1 ring-purple-200"
                  : "bg-purple-600 text-white shadow-md shadow-purple-600/25 hover:bg-purple-700 hover:shadow-lg hover:shadow-purple-600/30",
              )}
            >
              <ShoppingCart className="w-[18px] h-[18px] shrink-0" />
              {t("Comprar créditos")}
              {/* Saldo baixo ou esgotado: ponto de alerta no CTA. */}
              {tab !== "plans" && (isEmpty || isLow) && (
                <span
                  className={cn(
                    "absolute -top-1 -right-1 w-3 h-3 rounded-full ring-2 ring-white",
                    isEmpty ? "bg-rose-500" : "bg-amber-400",
                  )}
                  aria-label={t("Requer atenção")}
                />
              )}
            </button>
          </div>

          <div className="mt-3 -mx-4 sm:-mx-5 border-t border-purple-100 flex items-center">
            <div
              role="tablist"
              aria-label={t("Seções de créditos")}
              className="flex-1 min-w-0 px-2 sm:px-3 flex overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {tabs.map((item, index) => {
                const active = item.id === tab;
                return (
                  <button
                    key={item.id}
                    ref={(el) => {
                      tabRefs.current[index] = el;
                    }}
                    type="button"
                    role="tab"
                    id={`credits-tab-${item.id}`}
                    aria-controls="credits-tabpanel"
                    aria-selected={active}
                    tabIndex={active ? 0 : -1}
                    onClick={() => setTab(item.id)}
                    onKeyDown={(e) => handleTabKeyDown(e, index)}
                    className={cn(
                      "relative shrink-0 flex items-center gap-2 px-3 py-3 rounded-t-lg text-sm font-semibold whitespace-nowrap transition-colors focus-visible:outline-hidden focus-visible:bg-purple-50",
                      active
                        ? "text-purple-700"
                        : "text-gray-500 hover:text-gray-900",
                    )}
                  >
                    <span
                      className={cn(
                        "shrink-0 [&>svg]:w-4 [&>svg]:h-4",
                        active ? "text-purple-600" : "text-gray-400",
                      )}
                    >
                      {item.icon}
                    </span>
                    {item.label}
                    {item.count && (
                      <span
                        className={cn(
                          "rounded-full px-1.5 py-px text-[11px] font-bold tabular-nums",
                          active
                            ? "bg-purple-100 text-purple-700"
                            : "bg-gray-100 text-gray-600",
                        )}
                      >
                        {item.count}
                      </span>
                    )}
                    {active && (
                      <span className="absolute inset-x-2 bottom-0 h-[3px] rounded-t-full bg-purple-600" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Pagamento da assinatura em atraso: aviso na carência, erro depois do bloqueio. */}
      {paymentOverdue && (
        <Alert
          variant={paymentOverdue.blocked ? "error" : "warning"}
          title={
            paymentOverdue.blocked
              ? t("Ações com IA bloqueadas por pagamento em atraso")
              : t("Pagamento da assinatura em atraso")
          }
        >
          {paymentOverdue.blocked
            ? t(
                "Regularize o pagamento pelo link que o Asaas enviou por e-mail. As ações com IA voltam assim que o pagamento for confirmado; envios de WhatsApp e e-mail continuam funcionando.",
              )
            : t(
                "Regularize até {date} pelo link que o Asaas enviou por e-mail para não bloquear as ações com IA.",
                {
                  date: new Date(paymentOverdue.blocks_at).toLocaleDateString(
                    "pt-BR",
                  ),
                },
              )}
        </Alert>
      )}

      {/* Alerta de saldo logo abaixo do cabeçalho, visível em qualquer aba */}
      {(isEmpty || isLow) && (
        <Alert
          variant={isEmpty ? "error" : "warning"}
          title={
            isEmpty
              ? t("Saldo de Créditos Esgotado")
              : t("Atenção ao Saldo de Créditos")
          }
          action={
            tab !== "plans" && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => goToPlans()}
                className={CARD_PRIMARY}
                icon={<ShoppingCart className="w-3.5 h-3.5" />}
              >
                {t("Comprar créditos")}
              </Button>
            )
          }
        >
          {isEmpty
            ? t(
                "Você atingiu o limite de uso. Para continuar gerando imagens, vídeos e conteúdos com inteligência artificial, é necessário adquirir novos créditos.",
              )
            : t(
                "Seu saldo está baixo ({balance} restantes). Algumas ações premium podem falhar se os créditos terminarem durante o processo.",
                { balance: creditsBalance },
              )}
        </Alert>
      )}

      <div
        id="credits-tabpanel"
        role="tabpanel"
        aria-labelledby={`credits-tab-${tab}`}
        className="space-y-4"
      >
        {tab === "overview" && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Kpi
                icon={Coins}
                label={t("Saldo disponível")}
                value={fmt(creditsBalance)}
                hint={t("créditos de IA")}
                valueClass={
                  isEmpty
                    ? "text-rose-600"
                    : isLow
                      ? "text-amber-600"
                      : "text-purple-700"
                }
              />
              <Kpi
                icon={TrendingDown}
                label={t("Consumidos")}
                value={fmt(consumed)}
                hint={t("créditos no total, sem estornos")}
              />
              <Kpi
                icon={Clock3}
                label={t("Duração estimada")}
                value={
                  daysLeft === null
                    ? "—"
                    : t("~{days} dias", { days: fmt(daysLeft) })
                }
                hint={
                  daysLeft === null
                    ? t("sem consumo nos últimos 30 dias")
                    : t("no ritmo dos últimos 30 dias")
                }
                valueClass={
                  daysLeft !== null && daysLeft < 7
                    ? "text-amber-600"
                    : undefined
                }
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* WhatsApp primeiro: é o produto de envio principal. */}
              <ProductCard
                icon={MessageCircle}
                label={t("WhatsApp (templates)")}
                value={productBalances.whatsapp}
                onBuy={() =>
                  goToPlans({ segment: "sends", product: "whatsapp" })
                }
                renewsAt={allowanceRenewsAt}
              />
              <ProductCard
                icon={Mail}
                label={t("E-mail Marketing")}
                value={productBalances.email}
                onBuy={() => goToPlans({ segment: "sends", product: "email" })}
                renewsAt={allowanceRenewsAt}
              />
            </div>
            {usageSummaryLoading ? (
              <SettingsUsageSummarySkeleton />
            ) : (
              usageSummary &&
              usageSummary.by_type.length > 0 && (
                <UsageSummaryCard summary={usageSummary} />
              )
            )}
            {isNewAccount && (
              <div className="rounded-2xl border border-purple-200 bg-white p-4">
                <h3 className="text-sm font-bold text-gray-900">
                  {t("Comece a usar seus créditos")}
                </h3>
                <p className="mt-0.5 text-sm text-gray-500">
                  {t(
                    "Cada ação com IA desconta créditos do saldo. Veja quanto custam as mais usadas:",
                  )}
                </p>
                <ul className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {STARTER_ACTIONS.filter((key) => costs[key] > 0).map(
                    (key) => {
                      const Icon = USAGE_TYPE_ICONS[key] ?? Coins;
                      return (
                        <li
                          key={key}
                          className="flex items-center gap-2 rounded-xl border border-purple-100 px-3 py-2"
                        >
                          <Icon className="w-4 h-4 text-purple-600 shrink-0" />
                          <span className="flex-1 text-sm text-gray-700 truncate">
                            {t(USAGE_TYPE_LABELS[key])}
                          </span>
                          <span className="text-sm font-bold text-gray-900 tabular-nums">
                            {costs[key]}{" "}
                            <span className="text-xs font-semibold text-gray-500">
                              {t(costs[key] === 1 ? "crédito" : "créditos")}
                            </span>
                          </span>
                        </li>
                      );
                    },
                  )}
                </ul>
                <button
                  type="button"
                  onClick={() => setTab("costs")}
                  className={cn(CARD_ACTION, "mt-3")}
                >
                  {t("Ver todos os custos")}
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            {(recentLoading || recent.length > 0) && (
              <div className="border border-purple-200 rounded-2xl overflow-hidden bg-white">
                <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-gray-50">
                  <h3 className="text-sm font-bold text-gray-900">
                    {t("Atividade recente")}
                  </h3>
                  <button
                    type="button"
                    onClick={() => setTab("history")}
                    className={CARD_ACTION}
                  >
                    {t("Ver histórico")}
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
                {recentLoading ? (
                  <SettingsHistoryRowsSkeleton rows={5} />
                ) : (
                  <DayGroupedList
                    rows={recent}
                    renderRow={(row, i) => (
                      <HistoryRow key={row.id ?? i} row={row} />
                    )}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {tab === "costs" &&
          (costEntries.length === 0 && costsError ? (
            <p className="rounded-2xl border border-purple-200 bg-white py-10 text-center text-sm font-semibold text-rose-600">
              {t(
                "Não foi possível carregar a tabela de custos. Tente novamente.",
              )}
            </p>
          ) : costEntries.length === 0 ? (
            <SettingsCostsSkeleton />
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative w-full max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="search"
                    value={costQuery}
                    onChange={(e) => setCostQuery(e.target.value)}
                    placeholder={t("Buscar ação")}
                    aria-label={t("Buscar ação")}
                    className="w-full h-9 rounded-lg border border-purple-200 bg-white pl-9 pr-9 text-sm text-gray-800 focus:outline-hidden focus:ring-2 focus:ring-purple-400 [&::-webkit-search-cancel-button]:hidden"
                  />
                  {costQuery && (
                    <button
                      type="button"
                      onClick={() => setCostQuery("")}
                      aria-label={t("Limpar busca")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 hover:bg-purple-50 hover:text-purple-700"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <div
                  role="radiogroup"
                  aria-label={t("Ordenar por")}
                  className="inline-flex rounded-lg border border-purple-200 bg-white p-0.5"
                >
                  {(
                    [
                      ["price", "Mais caras"],
                      ["usage", "Mais usadas"],
                      ["name", "A–Z"],
                    ] as const
                  ).map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      role="radio"
                      aria-checked={costSort === id}
                      onClick={() => setCostSort(id)}
                      className={cn(
                        "h-7 px-3 rounded-md text-xs font-semibold transition-colors",
                        costSort === id
                          ? "bg-purple-600 text-white"
                          : "text-gray-600 hover:bg-purple-50",
                      )}
                    >
                      {t(label)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notas: custos variáveis por modelo e o que sai do saldo de envios. */}
              <details
                open={notesOpen}
                onToggle={(e) =>
                  setNotesOpen((e.currentTarget as HTMLDetailsElement).open)
                }
                className="group rounded-2xl border border-purple-200 bg-white text-xs text-gray-600"
              >
                <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-800 [&::-webkit-details-marker]:hidden">
                  <Info className="w-4 h-4 text-purple-500 shrink-0" />
                  <span className="flex-1">
                    {t("Como os custos funcionam")}
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90" />
                </summary>
                <div className="space-y-2 px-4 pb-3">
                  {maxLlmMultiplier > 1 && (
                    <div>
                      <p>
                        {t(
                          "Ações de texto com IA mostram o custo base. Com modelos mais avançados, podem custar até {factor}× mais.",
                          { factor: fmt(maxLlmMultiplier) },
                        )}
                      </p>
                      <details className="mt-1">
                        <summary className="cursor-pointer font-semibold text-purple-700 hover:underline">
                          {t("Ver multiplicadores por modelo")}
                        </summary>
                        <ul className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1">
                          {modelMultipliers.map(([model, factor]) => (
                            <li
                              key={model}
                              className="flex justify-between gap-2 tabular-nums"
                            >
                              <span className="truncate text-gray-700">
                                {model}
                              </span>
                              <span className="font-semibold text-gray-900">
                                {fmt(factor)}×
                              </span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    </div>
                  )}
                  <p>
                    {t(
                      "Envios de templates do WhatsApp e de campanhas de e-mail não usam créditos: saem do saldo de envios.",
                    )}{" "}
                    <button
                      type="button"
                      onClick={() => goToPlans({ segment: "sends" })}
                      className="font-semibold text-purple-700 hover:underline"
                    >
                      {t("Ver pacotes de envio")}
                    </button>
                  </p>
                </div>
              </details>

              {costGroups.length === 0 ? (
                <p className="rounded-2xl border border-purple-200 bg-white py-6 text-center text-sm text-gray-500">
                  {t("Nenhuma ação encontrada")}
                </p>
              ) : (
                // Colunas (alvenaria): grupos de tamanhos diferentes se encaixam sem vãos.
                <div className="columns-1 lg:columns-2 gap-3">
                  {costGroups.map((group) => (
                    <section
                      key={group.label}
                      className="mb-3 break-inside-avoid rounded-2xl border border-purple-200 bg-white"
                      aria-label={t(group.label)}
                    >
                      <h3 className="px-4 pt-3 pb-1 text-sm font-bold text-gray-900">
                        {t(group.label)}
                      </h3>
                      <ul className="divide-y divide-gray-50">
                        {group.entries.map(([key, value]) => {
                          const Icon = USAGE_TYPE_ICONS[key] ?? Coins;
                          // Custo 0 (ação gratuita) não tem limite pelo saldo.
                          const affordable =
                            value > 0
                              ? Math.floor(creditsBalance / value)
                              : Number.POSITIVE_INFINITY;
                          const times = usedTimes(key);
                          // Só quando importa: com saldo folgado o número não ajuda.
                          const balanceNote =
                            affordable === 0 ? (
                              <span className="font-semibold text-rose-600">
                                {t("Saldo insuficiente")}
                              </span>
                            ) : affordable < 20 ? (
                              <span className="font-semibold text-amber-600">
                                {t("Dá para ~{count} com seu saldo", {
                                  count: fmt(affordable),
                                })}
                              </span>
                            ) : null;
                          return (
                            <li
                              key={key}
                              className="flex items-center gap-3 px-4 py-2.5"
                            >
                              <div className="w-8 h-8 shrink-0 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
                                <Icon className="w-4 h-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-800 truncate">
                                  <HighlightMatch
                                    text={t(USAGE_TYPE_LABELS[key] || key)}
                                    query={costQuery}
                                  />
                                </p>
                                {(balanceNote || times > 0) && (
                                  <p className="text-xs text-gray-500 truncate">
                                    {balanceNote}
                                    {balanceNote && times > 0 && " · "}
                                    {times > 0 &&
                                      t("usou ~{count}× em 30 dias", {
                                        count: fmt(times),
                                      })}
                                  </p>
                                )}
                              </div>
                              <span className="shrink-0 font-bold text-gray-900 tabular-nums">
                                {value}{" "}
                                <span className="text-xs font-semibold text-gray-500">
                                  {t(value === 1 ? "crédito" : "créditos")}
                                </span>
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </section>
                  ))}
                </div>
              )}
            </div>
          ))}

        {tab === "plans" && (
          <Suspense fallback={<SettingsPlansSkeleton />}>
            <CreditsPlansPanel
              subscription={subscription}
              segment={segment}
              onSegmentChange={(next) => setPlansParams({ segment: next })}
              product={product}
              onProductChange={(next) =>
                setPlansParams({ segment: "sends", product: next })
              }
              costs={costs}
              monthlyUsage={consumedLast30d}
              productBalances={productBalances}
              onCheckoutOpened={onCheckoutOpened}
            />
          </Suspense>
        )}

        {tab === "history" && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <div
                role="radiogroup"
                aria-label={t("Movimentações")}
                className="inline-flex rounded-lg border border-purple-200 bg-white p-0.5"
              >
                {(
                  [
                    ["usage", "Consumo"],
                    ["purchases", "Compras"],
                  ] as const
                ).map(([kind, label]) => (
                  <button
                    key={kind}
                    type="button"
                    role="radio"
                    aria-checked={historyFilter.kind === kind}
                    onClick={() =>
                      onHistoryFilterChange({
                        kind,
                        usageType: "",
                        days: historyFilter.days,
                      })
                    }
                    className={cn(
                      "h-7 px-3 rounded-md text-xs font-semibold transition-colors",
                      historyFilter.kind === kind
                        ? "bg-purple-600 text-white"
                        : "text-gray-600 hover:bg-purple-50",
                    )}
                  >
                    {t(label)}
                  </button>
                ))}
              </div>
              {!isPurchases && (
                <select
                  aria-label={t("Tipo de uso")}
                  value={historyFilter.usageType}
                  onChange={(e) =>
                    onHistoryFilterChange({
                      ...historyFilter,
                      usageType: e.target.value,
                    })
                  }
                  className="h-8 rounded-lg border border-purple-200 bg-white px-2.5 text-xs font-semibold text-gray-700 focus:outline-hidden focus:ring-2 focus:ring-purple-400"
                >
                  <option value="">{t("Todos os tipos")}</option>
                  {usageTypes.map((type) => (
                    <option key={type} value={type}>
                      {t(USAGE_TYPE_LABELS[type] || type)}
                    </option>
                  ))}
                </select>
              )}
              <div
                role="radiogroup"
                aria-label={t("Período")}
                className="inline-flex rounded-lg border border-purple-200 bg-white p-0.5"
              >
                {HISTORY_PERIODS.map((period) => {
                  const active = historyFilter.days === period.days;
                  return (
                    <button
                      key={period.label}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() =>
                        onHistoryFilterChange({
                          ...historyFilter,
                          days: period.days,
                        })
                      }
                      className={cn(
                        "h-7 px-3 rounded-md text-xs font-semibold transition-colors",
                        active
                          ? "bg-purple-600 text-white"
                          : "text-gray-600 hover:bg-purple-50",
                      )}
                    >
                      {t(period.label)}
                    </button>
                  );
                })}
              </div>
              {hasHistoryFilter && (
                <button
                  type="button"
                  onClick={() =>
                    onHistoryFilterChange({
                      kind: historyFilter.kind,
                      usageType: "",
                      days: null,
                    })
                  }
                  className={CARD_ACTION}
                >
                  <X className="w-3.5 h-3.5" />
                  {t("Limpar filtros")}
                </button>
              )}
              {!isPurchases && (
                <button
                  type="button"
                  onClick={onExportCsv}
                  disabled={exporting || history.length === 0}
                  className={cn(CARD_ACTION, "sm:ml-auto")}
                >
                  <ArrowDownToLine className="w-3.5 h-3.5" />
                  {exporting ? t("Exportando...") : t("Exportar CSV")}
                </button>
              )}
            </div>
            <div className="border border-purple-200 rounded-2xl overflow-hidden bg-white">
              {loading ? (
                <SettingsHistoryRowsSkeleton rows={10} />
              ) : history.length === 0 ? (
                <EmptyState
                  icon={<Coins className="h-12 w-12" />}
                  title={
                    hasHistoryFilter
                      ? t("Nenhum registro para esses filtros")
                      : t("Nenhum registro encontrado")
                  }
                />
              ) : (
                <DayGroupedList
                  rows={history}
                  renderRow={(row, i) =>
                    isPurchases ? (
                      <PurchaseRow key={row.id ?? i} row={row} />
                    ) : (
                      <HistoryRow key={row.id ?? i} row={row} />
                    )
                  }
                />
              )}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => onPageChange(Math.max(1, page - 1))}
                  disabled={page <= 1 || loading}
                  className={CARD_ACTION}
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  {t("Anterior")}
                </button>
                <span className="px-2 text-xs font-semibold text-gray-500 tabular-nums">
                  {page} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages || loading}
                  className={CARD_ACTION}
                >
                  {t("Próxima")}
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeContext } = useActiveContext();
  const [balance, setBalance] = useState<number | null>(null);
  const [consumed, setConsumed] = useState<number>(0);
  const [consumedLast30d, setConsumedLast30d] = useState<number>(0);
  const [productBalances, setProductBalances] = useState<
    Record<"email" | "whatsapp", ProductBalance>
  >({ email: EMPTY_PRODUCT_BALANCE, whatsapp: EMPTY_PRODUCT_BALANCE });
  const [history, setHistory] = useState<any[]>([]);
  const [recent, setRecent] = useState<any[]>([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [costs, setCosts] = useState<Record<string, number>>({});
  const [costsError, setCostsError] = useState(false);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  // `loading` = só o saldo (cabeçalho e Visão geral); histórico e custos carregam nas abas.
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [usageTypes, setUsageTypes] = useState<string[]>([]);
  const [exporting, setExporting] = useState(false);
  const [usageSummary, setUsageSummary] = useState<UsageSummary | null>(null);
  const [usageSummaryLoading, setUsageSummaryLoading] = useState(true);
  const [paymentOverdue, setPaymentOverdue] = useState<PaymentOverdue | null>(
    null,
  );
  const [llmMultipliers, setLlmMultipliers] = useState<Record<string, number>>(
    {},
  );
  const [allowanceRenewsAt, setAllowanceRenewsAt] = useState<string | null>(
    null,
  );

  // Filtros do histórico vêm da URL (sobrevivem ao recarregar e podem ser compartilhados).
  const historyFilter = readHistoryFilter(searchParams);
  const { kind, usageType, days } = historyFilter;

  const loadBalance = useCallback(async () => {
    try {
      const res = (await apiService.get(API_ENDPOINTS.CREDITS.BALANCE)) as any;
      if (res && typeof res.balance === "number") {
        setBalance(res.balance);
        setConsumed(res.consumed || 0);
        setConsumedLast30d(res.consumed_last_30d || 0);
        setProductBalances({
          email: res.product_balances?.email ?? EMPTY_PRODUCT_BALANCE,
          whatsapp: res.product_balances?.whatsapp ?? EMPTY_PRODUCT_BALANCE,
        });
        setSubscription(res.subscription ?? null);
        setAllowanceRenewsAt(res.allowance_renews_at ?? null);
        setPaymentOverdue(res.payment_overdue ?? null);
      } else {
        setBalance(0);
        setConsumed(0);
        setConsumedLast30d(0);
      }
    } catch {
      setBalance(0);
      setConsumed(0);
      setConsumedLast30d(0);
    }
  }, []);

  // Tipos de uso (opções do filtro) só precisam vir uma vez por contexto.
  const usageTypesLoadedRef = useRef(false);

  const loadHistory = useCallback(async (p: number, filter: HistoryFilter) => {
    try {
      const params = new URLSearchParams({ page: String(p), per_page: "10" });
      if (filter.days) params.set("days", String(filter.days));
      if (filter.kind === "usage") {
        if (filter.usageType) params.set("usage_type", filter.usageType);
        if (!usageTypesLoadedRef.current) params.set("include_types", "1");
      }
      const endpoint =
        filter.kind === "purchases"
          ? API_ENDPOINTS.CREDITS.PURCHASES
          : API_ENDPOINTS.CREDITS.HISTORY;
      const data = (await apiService.get(
        `${endpoint}?${params.toString()}`,
      )) as {
        items?: any[];
        total?: number;
        pages?: number;
        usage_types?: string[];
      };
      setHistory(data.items ?? []);
      if (data.usage_types) {
        setUsageTypes(data.usage_types);
        usageTypesLoadedRef.current = true;
      }
      setTotal(typeof data.total === "number" ? data.total : 0);
      setTotalPages(typeof data.pages === "number" ? data.pages : 0);
    } catch {
      setHistory([]);
    }
  }, []);

  const loadCosts = useCallback(async () => {
    try {
      const res = await apiService.get(API_ENDPOINTS.CREDITS.COSTS);
      const data = res as {
        costs?: Record<string, number>;
        llm_multipliers?: Record<string, number>;
      };
      if (data?.costs && typeof data.costs === "object") {
        setCosts(data.costs);
        setLlmMultipliers(data.llm_multipliers ?? {});
        setCostsError(false);
      } else {
        setCostsError(true);
      }
    } catch {
      setCosts({});
      setCostsError(true);
    }
  }, []);

  // Saldo: é o que segura o skeleton da página (cabeçalho, chips e Visão geral).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadBalance().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [loadBalance, activeContext]);

  // Custos: em paralelo; a aba e as estimativas mostram skeleton até chegar.
  useEffect(() => {
    loadCosts();
  }, [loadCosts, activeContext]);

  // Depois de abrir o pagamento (outra aba), atualiza o saldo sempre que o usuário volta
  // para esta aba — os créditos entram quando o Asaas confirma, sem precisar recarregar.
  const awaitingPaymentRef = useRef(false);
  const handleCheckoutOpened = useCallback(() => {
    awaitingPaymentRef.current = true;
  }, []);
  useEffect(() => {
    const onFocus = () => {
      if (awaitingPaymentRef.current) loadBalance();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadBalance]);

  // Resumo dos últimos 30 dias (gráfico diário e consumo por tipo) da Visão geral.
  useEffect(() => {
    let cancelled = false;
    setUsageSummaryLoading(true);
    apiService
      .get(API_ENDPOINTS.CREDITS.USAGE_SUMMARY)
      .then((res) => {
        if (!cancelled) setUsageSummary(res as UsageSummary);
      })
      .catch(() => {
        if (!cancelled) setUsageSummary(null);
      })
      .finally(() => {
        if (!cancelled) setUsageSummaryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeContext]);

  // Atividade recente: sempre as 5 últimas, sem os filtros do Histórico.
  useEffect(() => {
    let cancelled = false;
    setRecentLoading(true);
    apiService
      .get(`${API_ENDPOINTS.CREDITS.HISTORY}?page=1&per_page=5`)
      .then((res) => {
        if (!cancelled) setRecent((res as { items?: any[] }).items ?? []);
      })
      .catch(() => {
        if (!cancelled) setRecent([]);
      })
      .finally(() => {
        if (!cancelled) setRecentLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeContext]);

  // Histórico: uma única busca por página/filtro/contexto (antes a página 1 era buscada
  // duas vezes na abertura).
  useEffect(() => {
    usageTypesLoadedRef.current = false;
  }, [activeContext]);

  useEffect(() => {
    let cancelled = false;
    setHistoryLoading(true);
    loadHistory(page, { kind, usageType, days }).finally(() => {
      if (!cancelled) setHistoryLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [page, kind, usageType, days, loadHistory, activeContext]);

  /** Grava os filtros na URL (mantendo a aba) e volta para a página 1. */
  const handleHistoryFilterChange = useCallback(
    (next: HistoryFilter) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set("aba", "historico");
          params.delete("ver");
          params.delete("uso");
          params.delete("periodo");
          if (next.kind === "purchases") params.set("ver", "entradas");
          if (next.usageType) params.set("uso", next.usageType);
          if (next.days) params.set("periodo", String(next.days));
          return params;
        },
        { replace: true },
      );
      setPage(1);
    },
    [setSearchParams],
  );

  const handleExportCsv = useCallback(async () => {
    const params = new URLSearchParams();
    if (usageType) params.set("usage_type", usageType);
    if (days) params.set("days", String(days));
    setExporting(true);
    try {
      await apiService.downloadFile(
        `${API_ENDPOINTS.CREDITS.HISTORY_EXPORT}?${params.toString()}`,
        "historico-creditos.csv",
      );
    } catch {
      showToastWarning(t("Não foi possível exportar o histórico."));
    } finally {
      setExporting(false);
    }
  }, [usageType, days, t]);

  return (
    <PageLayout
      title={t("Créditos e consumo")}
      subtitle={t("Saldo, consumo, compras e planos da sua conta.")}
      containerSize="full"
      showContainer={true}
      padding="none"
      className="w-full px-4 py-4"
      loading={loading}
      skeleton={
        <SettingsSkeleton
          tab={TAB_SLUGS[searchParams.get("aba") ?? ""] ?? "overview"}
        />
      }
    >
      <div className="relative z-10">
        <CreditsDashboard
          balance={balance}
          productBalances={productBalances}
          history={history}
          costs={costs}
          costsError={costsError}
          loading={historyLoading}
          total={total}
          page={page}
          totalPages={totalPages}
          consumed={consumed}
          consumedLast30d={consumedLast30d}
          recent={recent}
          recentLoading={recentLoading}
          onPageChange={setPage}
          historyFilter={historyFilter}
          onHistoryFilterChange={handleHistoryFilterChange}
          usageTypes={usageTypes}
          subscription={subscription}
          onExportCsv={handleExportCsv}
          exporting={exporting}
          usageSummary={usageSummary}
          usageSummaryLoading={usageSummaryLoading}
          allowanceRenewsAt={allowanceRenewsAt}
          llmMultipliers={llmMultipliers}
          onCheckoutOpened={handleCheckoutOpened}
          paymentOverdue={paymentOverdue}
        />
      </div>
    </PageLayout>
  );
};

export default Settings;
